import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { MessageCircle, Send, ArrowLeft, Waves, User } from 'lucide-react';

export default function MessagesPage() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  const fetchThreads = useCallback(async () => {
    try {
      const res = await api.get('/messages/threads');
      setThreads(res.data);
    } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); } finally { setLoading(false); }
  }, []);

  const fetchMessages = useCallback(async (childId) => {
    try {
      const res = await api.get(`/messages?child_id=${childId}`);
      setMessages(res.data);
    } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); }
  }, []);

  useEffect(() => {
    fetchThreads();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchThreads]);

  useEffect(() => {
    if (selectedThread) {
      fetchMessages(selectedThread.child_id);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => fetchMessages(selectedThread.child_id), 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedThread, fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !selectedThread) return;
    setSending(true);
    try {
      await api.post('/messages', {
        child_id: selectedThread.child_id,
        text: newMsg.trim(),
      });
      setNewMsg('');
      fetchMessages(selectedThread.child_id);
      fetchThreads();
    } catch { toast.error('Failed to send message'); }
    finally { setSending(false); }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
           d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  if (loading) return <div className="flex items-center justify-center p-12"><Waves className="w-10 h-10 text-[#22D3EE] animate-pulse" /></div>;

  // Chat view when thread is selected
  if (selectedThread) {
    const otherName = selectedThread.other_user?.name || 'Unknown';
    const otherRole = selectedThread.other_user?.role || '';

    return (
      <div className="max-w-3xl mx-auto animate-fadeIn flex flex-col h-[calc(100vh-12rem)] lg:h-[calc(100vh-10rem)]" data-testid="chat-view">
        {/* Chat header */}
        <div className="flex items-center gap-3 pb-4 border-b border-slate-200 mb-4">
          <Button
            data-testid="chat-back-btn"
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={() => { setSelectedThread(null); fetchThreads(); }}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 rounded-full bg-[#1E1B4B] flex items-center justify-center text-white font-bold" style={{ fontFamily: 'Nunito' }}>
            {otherName.charAt(0).toUpperCase()}
          </div>
          <div>
            <h2 className="font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>{otherName}</h2>
            <p className="text-xs text-slate-500 capitalize">{otherRole} &mdash; Re: {selectedThread.child_name}</p>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-3 pr-2" data-testid="messages-list">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <MessageCircle className="w-12 h-12 mb-3 opacity-30" />
              <p className="font-semibold" style={{ fontFamily: 'Nunito' }}>No messages yet</p>
              <p className="text-sm">Start the conversation about {selectedThread.child_name}</p>
            </div>
          )}
          {messages.map(msg => {
            const isMine = msg.sender_id === user?.user_id;
            return (
              <div
                key={msg.message_id}
                className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${msg.message_id}`}
              >
                <div className={`max-w-[75%] ${isMine ? 'order-2' : ''}`}>
                  <div className={`rounded-2xl px-4 py-3 ${
                    isMine
                      ? 'bg-[#1E1B4B] text-white rounded-br-md'
                      : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                  </div>
                  <p className={`text-[11px] mt-1 ${isMine ? 'text-right text-slate-400' : 'text-slate-400'}`}>
                    {msg.sender_name} &middot; {formatTime(msg.created_at)}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <form onSubmit={sendMessage} className="flex gap-2 pt-4 border-t border-slate-200 mt-4" data-testid="message-input-form">
          <Input
            data-testid="message-input"
            value={newMsg}
            onChange={e => setNewMsg(e.target.value)}
            placeholder={`Message about ${selectedThread.child_name}...`}
            className="flex-1 rounded-full border-slate-200"
            disabled={sending}
          />
          <Button
            data-testid="send-message-btn"
            type="submit"
            disabled={sending || !newMsg.trim()}
            className="rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] w-11 h-11 p-0"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    );
  }

  // Thread list view
  return (
    <div className="max-w-3xl mx-auto animate-fadeIn" data-testid="messages-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>
          Messages
        </h1>
        <p className="text-slate-500 mt-1">
          {user?.role === 'instructor' ? 'Chat with parents about their children' : 'Chat with your child\'s instructor'}
        </p>
      </div>

      {threads.length === 0 ? (
        <Card className="rounded-3xl border-dashed border-2 border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <MessageCircle className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-xl font-bold text-slate-400" style={{ fontFamily: 'Nunito' }}>No Conversations</p>
            <p className="text-slate-400 mt-2 text-sm">
              {user?.role === 'instructor'
                ? 'Conversations will appear when you have children with assigned parents'
                : 'Conversations will appear when your child has an assigned instructor'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {threads.map(thread => (
            <Card
              key={thread.child_id}
              className="rounded-2xl border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer"
              onClick={() => setSelectedThread(thread)}
              data-testid={`thread-${thread.child_id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full bg-[#1E1B4B] flex items-center justify-center text-white font-bold text-lg" style={{ fontFamily: 'Nunito' }}>
                      {(thread.other_user?.name || thread.child_name || '?').charAt(0).toUpperCase()}
                    </div>
                    {thread.unread_count > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#C4F017] text-[#1E1B4B] text-xs font-bold rounded-full flex items-center justify-center">
                        {thread.unread_count}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-slate-800 truncate" style={{ fontFamily: 'Nunito' }}>
                        {thread.other_user?.name || 'No recipient'}
                      </h3>
                      {thread.last_message_at && (
                        <span className="text-xs text-slate-400 flex-shrink-0 ml-2">{formatTime(thread.last_message_at)}</span>
                      )}
                    </div>
                    <p className="text-xs text-[#22D3EE] font-semibold capitalize mb-1">
                      {thread.other_user?.role} &mdash; Re: {thread.child_name}
                    </p>
                    {thread.last_message ? (
                      <p className="text-sm text-slate-500 truncate">{thread.last_message}</p>
                    ) : (
                      <p className="text-sm text-slate-400 italic">No messages yet — start chatting!</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
